/**
 * A component to display a confirmation dialog for deleting an item.
 *
 * @module @ulta/components/ConfirmDelete
 * @memberof -Common
 */
import './ConfirmDelete.scss';

import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';

import Button from '@ulta/modules/Button/Button';

/**
 * A component to display a confirmation dialog for deleting an item.
 * This is intended to be used as content within an overlay (e.g., BottomSheet or Modal).
 *
 * @method
 * @param {ConfirmDeleteProps} props - React properties passed from composition
 * @returns {React.Component} - The ConfirmDelete component.
 */
export const ConfirmDelete = ( props ) => {
  const {
    subTitle = defaultProps.subTitle,
    confirmAction,
    cancelAction,
    deleteAction,
    onClose = defaultProps.onClose
  } = props;

  const handleConfirmClick = useCallback( () => {
    confirmAction?.onClick?.();
    onClose?.();
  },
  [confirmAction, onClose]
  );

  const handleCancelClick = useCallback( () => {
    cancelAction?.onClick?.();
    onClose?.();
  },
  [cancelAction, onClose]
  );

  return (
    <div className='ConfirmDelete'>
      { subTitle && (
        <div className='ConfirmDelete__sub-title'>
          <Text
            textStyle='body'
          >
            { subTitle }
          </Text>
        </div>
      ) }
      <div className='ConfirmDelete__actions'>
        {
          confirmAction && (
            <Button
              fullWidth
              action={ deleteAction }
              label={ confirmAction.label }
              ariaLabel={ confirmAction.ariaLabel || confirmAction.label }
              compact
              onClick={ handleConfirmClick }
            />
          )
        }
        {
          cancelAction && (
            <Button
              fullWidth
              secondary
              label={ cancelAction.label }
              ariaLabel={ cancelAction.ariaLabel || cancelAction.label }
              compact
              onClick={ handleCancelClick }
            />
          )
        }


      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ConfirmDeleteProps
 * @type {object}
 * @property {string} subTitle - The subtitle text to display in the confirmation dialog.
 * @property {object} confirmAction - The action object for the confirm button (e.g., { label, ariaLabel, onClick }).
 * @property {object} cancelAction - The action object for the cancel button (e.g., { label, ariaLabel, onClick }).
 * @property {object} deleteAction - The action object passed to the confirm button's action prop for analytics/tracking.
 * @property {function} onClose - The function to call to close the overlay.
 * @property {string} adaRegionLabel - The accessibility label for the component's root element.
 */
ConfirmDelete.propTypes = {
  subTitle: PropTypes.string,
  confirmAction: PropTypes.shape( {
    label: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
    onClick: PropTypes.func
  } ).isRequired,
  cancelAction: PropTypes.shape( {
    label: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
    onClick: PropTypes.func
  } ).isRequired,
  deleteAction: PropTypes.object,
  onClose: PropTypes.func,
  adaRegionLabel: PropTypes.string
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} subTitle='You are attempting to remove an item from your list. Are you sure you want to continue?' - The default subtitle text.
 * @property {function} onClose=()=>{} - The default empty function for onClose.
 */
export const defaultProps = {
  subTitle: 'You are attempting to remove an item from your list. Are you sure you want to continue?',
  onClose: () => {}
};

export default ConfirmDelete;